import { CaseData } from 'modules/cm/api/cases/types';
import { FIELDS } from 'modules/cm/fields/types';
import { format } from 'utils/date';
import { logicalMapBackToFront } from 'utils/form/formUtils';
import { LoanDetails } from 'modules/cm/domain/Case/LoanDetails';
import { Mapper } from 'modules/cm/mapper/types';
import affordabilityResultsMapper, { mapAffordabilityResultsToApi } from './AffordabilityResults/mapper';
import { fromDomainToApiMapper } from 'modules/cm/mapper/mappings';

const mapper: Mapper<CaseData, LoanDetails> = {
  mapApiToDomain: (api) => {
    const {
      dateSubmitted,
      applicant1,
      applicant2,
      property,
      adviserName,
      adviserPhone,
      adviserFee,
      adviserEmail,
      policies,
      assessmentDate,
      ...restCaseData
    } = api;

    return {
      ...restCaseData,
      [FIELDS.MONTHLY_BASIC_SPEND]: restCaseData.monthlyBasicSpend,
      [FIELDS.OWNER]: restCaseData.assignedUserId ? restCaseData.assignedUserId.toString() : null,
      [FIELDS.HAS_ADVERSE_CREDIT]:
        typeof restCaseData.hasAdverseCredit === 'boolean'
          ? logicalMapBackToFront(restCaseData.hasAdverseCredit)
          : null,
      [FIELDS.IS_CREDIT_POLICY_EXEMPTION]:
        typeof restCaseData.isCreditPolicyExemption === 'boolean'
          ? logicalMapBackToFront(restCaseData.isCreditPolicyExemption)
          : null,
      [FIELDS.FCA_LOAN_PURPOSE]: restCaseData.fcaLoanPurpose,
      [FIELDS.OUTSTANDING_MORTGAGE_AMOUNT]: restCaseData.outstandingMortgageAmount,
      [FIELDS.SOURCE_OF_FUNDS_TO_CLEAR_OUTSTANDING_MORTGAGE]: restCaseData.sourceOfFundsToClearOutstandingMortgage,
      [FIELDS.EXISTING_MORTGAGE_STATUS]: restCaseData.existingMortgageStatus,
      [FIELDS.DELEGATED_AUTHORITY_LEVEL]: restCaseData.delegatedAuthorityLevel,
      [FIELDS.INITIAL_ASSESSMENT_APPROVED_BY]: restCaseData.approvedBy,
      [FIELDS.INITIAL_ASSESSMENT_APPROVED_DATE]: restCaseData.approvedDate
        ? format(restCaseData.approvedDate).fromBackendToView()
        : null,
      [FIELDS.INITIAL_ASSESSMENT_BY]: restCaseData.initialAssessmentBy,
      [FIELDS.INITIAL_ASSESSMENT_DATE]: assessmentDate ? format(assessmentDate).fromBackendToView() : null,
      [FIELDS.UW_RECOMMENDATION]: restCaseData.uwRecommendation,
      [FIELDS.UW_SPECIAL_CONDITIONS]: restCaseData.uwSpecialConditions,
      [FIELDS.RE_INSPECTION_FEE]: restCaseData.reinspectionFee,
      [FIELDS.AFT_PREFIX]: affordabilityResultsMapper.mapApiToDomain(restCaseData),
      [FIELDS.IS_PEPPER_UPLOAD_APPROVED]: restCaseData.isPepperUploadApproved,
      [FIELDS.TERM_LENGTH]: restCaseData.productTermLength,
    };
  },

  mapDomainFieldToAPI: (field, value) => {
    const { mapYesNo, mapNumber, mapDate } = fromDomainToApiMapper;
    const map = new Map<keyof LoanDetails, (value: any) => any>([
      [FIELDS.OWNER, (value: string) => (value === '' ? null : value)],
      [FIELDS.IS_CREDIT_POLICY_EXEMPTION, mapYesNo],
      [FIELDS.HAS_ADVERSE_CREDIT, mapYesNo],
      [FIELDS.OUTSTANDING_MORTGAGE_AMOUNT, mapNumber],
      [FIELDS.ANNUAL_COUNCIL_TAX, mapNumber],
      [FIELDS.ANNUAL_GROUND_RENT, mapNumber],
      [FIELDS.LOAN_AMOUNT_PURCHASE, mapNumber],
      [FIELDS.LOAN_AMOUNT_REMORTGAGE, mapNumber],
      [FIELDS.LOAN_DEBT_CONSOLIDATION, mapNumber],
      [FIELDS.LOAN_HOME_IMPROVEMENTS, mapNumber],
      [FIELDS.LOAN_DISCRETIONARY_SPEND, mapNumber],
      [FIELDS.LOAN_ASSISTING_OTHERS, mapNumber],
      [FIELDS.LOAN_OTHER_PURPOSES, mapNumber],
      [FIELDS.NET_LOAN, mapNumber],
      [FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE, mapNumber],
      [FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE, mapNumber],
      [FIELDS.ACTUAL_PROPERTY_VALUE, mapNumber],
      [FIELDS.INITIAL_ASSESSMENT_APPROVED_DATE, mapDate],
      [FIELDS.INITIAL_ASSESSMENT_DATE, mapDate],
      [FIELDS.RE_INSPECTION_FEE, mapNumber],
      [FIELDS.TERM_LENGTH, mapNumber],
    ]);
    const mapper = map.get(field as keyof LoanDetails);

    return mapper ? mapper(value) : value;
  },

  mapDomainNameToAPI: (field) => {
    const map = new Map<keyof LoanDetails, keyof CaseData>([
      [FIELDS.INITIAL_ASSESSMENT_BY, 'initialAssessmentBy'],
      [FIELDS.INITIAL_ASSESSMENT_APPROVED_DATE, 'approvedDate'],
      [FIELDS.INITIAL_ASSESSMENT_APPROVED_BY, 'approvedBy'],
      [FIELDS.INITIAL_ASSESSMENT_DATE, 'assessmentDate'],
      [FIELDS.DELEGATED_AUTHORITY_LEVEL, 'delegatedAuthorityLevel'],
      [FIELDS.TERM_LENGTH, 'productTermLength'],
    ]);

    const name = map.get(field as keyof LoanDetails);

    return name || field;
  },
};

export const mapLoanDetailsDomainToApi = (form: LoanDetails): any => {
  const { mapYesNo, mapNumber, mapDate } = fromDomainToApiMapper;
  return {
    assignedUserId: form[FIELDS.OWNER] === '' ? null : mapNumber(form[FIELDS.OWNER]),
    hasAdverseCredit: mapYesNo(form[FIELDS.HAS_ADVERSE_CREDIT]),
    isCreditPolicyExemption: mapYesNo(form[FIELDS.IS_CREDIT_POLICY_EXEMPTION]),
    outstandingMortgageAmount: mapNumber(form[FIELDS.OUTSTANDING_MORTGAGE_AMOUNT]),
    annualCouncilTax: mapNumber(form[FIELDS.ANNUAL_COUNCIL_TAX]),
    annualGroundRent: mapNumber(form[FIELDS.ANNUAL_GROUND_RENT]),
    loanAmountPurchase: mapNumber(form[FIELDS.LOAN_AMOUNT_PURCHASE]),
    loanAmountRemortgage: mapNumber(form[FIELDS.LOAN_AMOUNT_REMORTGAGE]),
    loanDebtConsolidation: mapNumber(form[FIELDS.LOAN_DEBT_CONSOLIDATION]),
    loanHomeImprovements: mapNumber(form[FIELDS.LOAN_HOME_IMPROVEMENTS]),
    loanDiscretionarySpend: mapNumber(form[FIELDS.LOAN_DISCRETIONARY_SPEND]),
    loanAssistingOthers: mapNumber(form[FIELDS.LOAN_ASSISTING_OTHERS]),
    loanOtherPurposes: mapNumber(form[FIELDS.LOAN_OTHER_PURPOSES]),
    netLoan: mapNumber(form[FIELDS.NET_LOAN]),
    estimatedPropertyValue: mapNumber(form[FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE]),
    propertyPurchasePrice: mapNumber(form[FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE]),
    actualPropertyValue: mapNumber(form[FIELDS.ACTUAL_PROPERTY_VALUE]),
    approvedDate: mapDate(form[FIELDS.INITIAL_ASSESSMENT_APPROVED_DATE]),
    assessmentDate: mapDate(form[FIELDS.INITIAL_ASSESSMENT_DATE]),
    reinspectionFee: mapNumber(form[FIELDS.RE_INSPECTION_FEE]),
    delegatedAuthorityLevel: form[FIELDS.DELEGATED_AUTHORITY_LEVEL],
    productCode: form[FIELDS.PRODUCT_CODE] ? form[FIELDS.PRODUCT_CODE] : null,
    productTier: form[FIELDS.PRODUCT_TIER],
    productTermLength: form[FIELDS.TERM_LENGTH],
    productType: form[FIELDS.PRODUCT_TYPE],
    productRepaymentType: form[FIELDS.PRODUCT_REPAYMENT_TYPE],
    status: form[FIELDS.STATUS],
    initialAssessmentBy: form[FIELDS.INITIAL_ASSESSMENT_BY],
    approvedBy: form[FIELDS.INITIAL_ASSESSMENT_APPROVED_BY],
    uwRecommendation: form[FIELDS.UW_RECOMMENDATION],
    uwSpecialConditions: form[FIELDS.UW_SPECIAL_CONDITIONS],
    id: form.id,
    applicationType: form[FIELDS.APPLICATION_TYPE],
    delphiScore: form.delphiScore,
    sourceOfDeposit: form.sourceOfDeposit,
    isPrivateSale: form.isPrivateSale,
    isFirstTimeBuyer: form.isFirstTimeBuyer,
    isBuyToLet: form.isBuyToLet,
    purposeOfCapitalRaise: form.purposeOfCapitalRaise,
    creditPolicyExemptionRationale: form.creditPolicyExemptionRationale,
    sortCode: form.sortCode,
    accountName: form.accountName,
    bankOrBuildingSocietyName: form.bankOrBuildingSocietyName,
    purposeOfTheLoan: form.purposeOfTheLoan,
    pepperPurposeOfTheLoan: form.pepperPurposeOfTheLoan,
    valuationFeePayment: form[FIELDS.VALUATION_FEE_PAYMENT],
    reInspectionFee: form[FIELDS.RE_INSPECTION_FEE],
    currentMortgageProvider: form[FIELDS.CURRENT_MORTGAGE_PROVIDER],
    postcode: form.postcode,
    productFeePaymentMethod: form.productFeePaymentMethod,
    createdBy: form.createdBy,
    accountNumber: form.accountNumber,
    monthlyBasicSpend: form.monthlyBasicSpend,
    stage: form.stage,
    updatedAt: form.updatedAt,
    depositSourceBuilders: form[FIELDS.DEPOSIT_SOURCE_BUILDERS],
    depositSourceDetails: form[FIELDS.DEPOSIT_SOURCE_DETAILS],
    depositSourceEquityFromSale: form[FIELDS.DEPOSIT_SOURCE_EQUITY_FROM_SALE],
    depositSourceGifts: form[FIELDS.DEPOSIT_SOURCE_GIFTS],
    depositSourceOther: form[FIELDS.DEPOSIT_SOURCE_OTHER],
    depositSourceSavings: form[FIELDS.DEPOSIT_SOURCE_SAVINGS],
    hasMarketingEmail: form[FIELDS.HAS_MARKETING_EMAIL],
    hasMarketingPost: form[FIELDS.HAS_MARKETING_POST],
    hasMarketingPhone: form[FIELDS.HAS_MARKETING_PHONE],
    hasMarketingSms: form[FIELDS.HAS_MARKETING_SMS],
    fcaLoanPurpose: form[FIELDS.FCA_LOAN_PURPOSE],
    sourceOfFundsToClearOutstandingMortgage: form[FIELDS.SOURCE_OF_FUNDS_TO_CLEAR_OUTSTANDING_MORTGAGE],
    existingMortgageStatus: form[FIELDS.EXISTING_MORTGAGE_STATUS],
    additionalDetails: form[FIELDS.ADDITIONAL_DETAILS],
    isPepperUploadApproved: form[FIELDS.IS_PEPPER_UPLOAD_APPROVED],
    isOnHoldStatus: form[FIELDS.IS_ON_HOLD_STATUS],
    isApprovedWithAuthorityLevels: form[FIELDS.IS_APPLICATION_APPROVED_AUTHORITY_LEVELS],
    isPreOfferCarriedOutForApplicants: form[FIELDS.IS_CALL_CARRIED_ALL_APPLICANTS],
    ...mapAffordabilityResultsToApi(form[FIELDS.AFT_PREFIX]),
  };
};

export default mapper;
